import { Component, HostBinding, Inject, Injector, Optional, TemplateRef, Type } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { isObservable } from 'rxjs';
import { take } from 'rxjs/operators';
import { CheckboxItem } from '../sg-checkbox/sg-checkbox.model';
import { isStorybookEnvironment } from '../storybook-utils';
import {
  SgDialogAction,
  SgDialogBadge,
  SgDialogButton,
  SgDialogData,
  SgDialogIconType,
  SgDialogLayout,
  SgDialogResult,
} from './sg-dialog.model';

@Component({
  selector: 'sg-dialog',
  templateUrl: './sg-dialog.component.html',
  styleUrls: ['./sg-dialog.component.scss'],
  standalone: false,
})
export class SgDialogComponent {
  //#region Members
  title: string;
  description: string;
  icon?: string;
  iconColor: string = 'primary';
  iconType: SgDialogIconType = 'material';
  layout: SgDialogLayout = 'horizontal';
  titleBadges?: SgDialogBadge[];
  titleBadgeDivider = false;
  okButton?: SgDialogButton;
  cancelButton?: SgDialogButton;
  extraButton?: SgDialogButton;
  showCloseButton = true;
  displayDontShowAgainCheckbox = false;
  dontShowAgainChecked = false;
  showTitleTooltipOnOverflow = false;
  slotContent?: TemplateRef<any> | Type<any>;
  compactSlot = false;
  assetsPath: string;
  //#endregion

  //#region Ctor
  constructor(
    @Optional() public dialogRef: MatDialogRef<SgDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: SgDialogData,
    public injector: Injector
  ) {
    this.setAssetsPath();
    this.setDialogData(data);
  }
  //#endregion

  //#region Getters
  get hasButtons(): boolean {
    return !!(this.okButton || this.cancelButton || this.extraButton);
  }

  get hasTwoButtons(): boolean {
    return !!(this.okButton && this.cancelButton);
  }

  @HostBinding('class.has-dont-show-again-checkbox')
  get hasDontShowAgainCheckbox(): boolean {
    return this.displayDontShowAgainCheckbox;
  }
  //#endregion

  //#region Public Methods
  async onOkButtonClick(): Promise<void> {
    await this.handleButtonClick(this.okButton!, SgDialogAction.Ok);
  }

  async onCancelButtonClick(): Promise<void> {
    await this.handleButtonClick(this.cancelButton!, SgDialogAction.Cancel);
  }

  async onExtraButtonClick(): Promise<void> {
    await this.handleButtonClick(this.extraButton!, SgDialogAction.Extra);
  }

  isTemplateRef(slotContent: any): boolean {
    return slotContent instanceof TemplateRef;
  }

  onDontShowAgainChange(event: CheckboxItem[]): void {
    this.dontShowAgainChecked = (event?.[0] as CheckboxItem).checked;
  }

  closeDialog(action: SgDialogAction = SgDialogAction.Close): void {
    this.dialogRef?.close({
      action,
      dontShowAgainChecked: this.displayDontShowAgainCheckbox && this.dontShowAgainChecked,
    } as SgDialogResult);
  }
  //#endregion

  //#region Private Methods
  private setAssetsPath() {
    this.assetsPath = isStorybookEnvironment()
      ? './' // In Storybook, assets are served from the root
      : '/assets/';
  }

  private setDialogData(data: SgDialogData) {
    if (data) {
      this.title = data.title;
      this.description = data.description;
      this.icon = data.icon;
      this.iconType = data.iconType || 'material';
      this.layout = data.layout || 'horizontal';
      this.titleBadges = data.titleBadges;
      this.titleBadgeDivider = data.titleBadgeDivider ?? false;
      this.okButton = data.okButton;
      this.cancelButton = data.cancelButton;
      this.extraButton = data.extraButton;
      this.showCloseButton = data.showCloseButton !== undefined ? data.showCloseButton : true;
      this.displayDontShowAgainCheckbox = data.displayDontShowAgainCheckbox ?? false;
      this.showTitleTooltipOnOverflow = data.showTitleTooltipOnOverflow ?? false;
      this.iconColor = data.iconColor || 'primary';
      this.compactSlot = data.compactSlot ?? false;
    }
  }

  private async handleButtonClick(button: SgDialogButton, action: SgDialogAction): Promise<void> {
    if (typeof button?.onClick === 'function') {
      const result = button.onClick();

      if (result instanceof Promise || isObservable(result)) {
        button.isLoading = true;

        try {
          if (isObservable(result)) {
            await result.pipe(take(1)).toPromise();
          } else {
            await result;
          }

          button.isLoading = false;

          if (!button.preventAutoClose) {
            this.closeDialog(action);
          }
        } catch (error) {
          button.isLoading = false;
          console.error('Dialog action failed:', error);
        }
      } else {
        if (!button.preventAutoClose) {
          this.closeDialog(action);
        }
      }
    } else {
      this.closeDialog(action);
    }
  }
  //#endregion
}
